import { FC, useRef } from 'react';
import {
  styled,
  Box,
  Button,
  CircularProgress,
  type Theme,
} from '@mui/material';
import { PhotoOutlined } from '@mui/icons-material';
import { UploadType } from 'further-types/files';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { Column, Row } from 'further-ui/components/Layout';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';

export type LogoValue = {
  previewUri: string;
  objectKey: string;
};

const StyledColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
  justifyContent: 'flex-start',
  alignItems: isCompact ? 'center' : 'flex-start',
  flexDirection: isCompact ? 'row' : 'column',
}));

export const LogoPreviewBoxLogo = styled('img', {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'isCompact',
})<{ variant: 'light' | 'dark'; isCompact?: boolean }>(
  ({
    theme,
    variant,
    isCompact,
  }: {
    theme: Theme;
    variant: 'light' | 'dark';
    isCompact?: boolean;
  }) => ({
    maxHeight: isCompact ? 30 : 100,
    maxWidth: '90%',
    border: isCompact
      ? undefined
      : `1px dashed ${theme.palette.grey[variant === 'light' ? 400 : 700]}`,
  }),
);

export const LogoPreviewBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'isCompact' &&
    prop !== 'customBgColor' &&
    prop !== 'disabled',
})<{
  variant: 'light' | 'dark';
  isCompact?: boolean;
  customBgColor?: string;
  disabled?: boolean;
}>(({ theme, variant, isCompact, customBgColor, disabled }) => ({
  width: isCompact ? 40 : '100%',
  cursor: disabled ? 'default' : 'pointer',
  minHeight: isCompact ? 40 : 200,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: customBgColor?.trim()
    ? customBgColor
    : variant === 'light'
    ? theme.palette.grey[200]
    : theme.palette.grey[800],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  value: LogoValue | null | undefined;
  onChange: (value: LogoValue | undefined) => void;
  boxVariant: 'light' | 'dark';
  isCompact?: boolean;
  backgroundColor?: string;
  allowRemove?: boolean;
  disabled?: boolean;
};

const LogoUploadBox: FC<Props> = ({
  value,
  onChange,
  boxVariant,
  isCompact = false,
  backgroundColor,
  allowRemove = false,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const notification = useNotification();
  const { loading: isUploading, uploadFile } = useFileUpload();

  const acceptedFileTypes = getValidFileTypesForUploadType(
    UploadType.FirmLogos,
  ).join(',');

  const handleLogoUpload = (file?: File) => {
    if (!file) return;

    const maxSize = getMaxSizeForUploadType(UploadType.FirmLogos, 'kb');
    if (file.size / 1000 > maxSize) {
      notification.error(`File size must be less than ${maxSize}KB`);
      return;
    }

    const previousValue = value ?? undefined;
    if (previousValue?.previewUri.startsWith('blob:')) {
      URL.revokeObjectURL(previousValue.previewUri);
    }

    const blobUri = URL.createObjectURL(file);
    onChange({ previewUri: blobUri, objectKey: '' });

    const handleFileUpload = async (file: File) => {
      const { data, error } = await uploadFile(file, UploadType.FirmLogos);
      URL.revokeObjectURL(blobUri);
      if (error) {
        notification.error(error.message);
        onChange(previousValue);
        return;
      }
      onChange({ previewUri: data.url, objectKey: data.filePath });
    };

    void handleFileUpload(file);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={acceptedFileTypes}
        hidden
        onChange={(event) => {
          handleLogoUpload(event.target.files?.[0]);
        }}
      />
      <StyledColumn spacing="sm" isCompact={isCompact}>
        <LogoPreviewBox
          variant={boxVariant}
          isCompact={isCompact}
          customBgColor={backgroundColor}
          disabled={disabled}
          onClick={disabled ? undefined : () => inputRef.current?.click()}
        >
          {isUploading ? (
            <CircularProgress size={24} color="info" />
          ) : value?.previewUri ? (
            <LogoPreviewBoxLogo
              variant={boxVariant}
              src={value.previewUri}
              isCompact={isCompact}
            />
          ) : (
            <PhotoOutlined fontSize={isCompact ? 'medium' : 'large'} />
          )}
        </LogoPreviewBox>
        {!disabled && (
          <Row justify="space-between" centered fullWidth>
            <Button
              size="large"
              variant="contained"
              disabled={isUploading}
              color="primary"
              onClick={() => inputRef.current?.click()}
            >
              Upload logo
            </Button>
            {allowRemove && value?.previewUri ? (
              <Button
                size="large"
                variant="outlined"
                disabled={isUploading}
                onClick={() => {
                  onChange(undefined);
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                Remove logo
              </Button>
            ) : null}
          </Row>
        )}
      </StyledColumn>
    </>
  );
};

export default LogoUploadBox;
